import { useEffect, useRef, useState } from "react";
import { serviceLadLogoHtml } from "../../../lib/brandLogo.js";
import { goToLookup } from "./goToLookup";
import { SL_HEADER_CSS, SL_HEADER_CTA, SL_HEADER_LINKS, SL_HEADER_MORE_LINKS, isActiveHeaderLink } from "./slHeader.js";

// React rendering of the shared ServiceLad header (see slHeader.js). Same
// markup and CSS as the server-rendered pages; only the menu state lives here.
const LOGO_HTML = serviceLadLogoHtml(22);

function Arrow() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12h14" />
      <path d="m13 6 6 6-6 6" />
    </svg>
  );
}

export default function SlCloudNav({ ctaHref = SL_HEADER_CTA.href, ctaLabel = SL_HEADER_CTA.label }) {
  const [open, setOpen] = useState(false);
  const headerRef = useRef(null);
  const path = typeof window === "undefined" ? "" : window.location.pathname;

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (e) => {
      if (headerRef.current && !headerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", onPointer);
    return () => document.removeEventListener("pointerdown", onPointer);
  }, [open]);

  // On the homepage, "/#lookup" and other anchors scroll in place instead of reloading.
  const onLinkClick = (event, href) => {
    setOpen(false);
    if (!href.startsWith("/#")) return;
    if (window.location.pathname !== "/" && window.location.pathname !== "/index.html") return;
    event.preventDefault();
    const hash = href.slice(2);
    if (hash === "lookup") goToLookup();
    else document.getElementById(hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const link = (item, className) => (
    <a
      key={item.href}
      className={className}
      href={item.href}
      aria-current={isActiveHeaderLink(item.href, path) ? "page" : undefined}
      onClick={(event) => onLinkClick(event, item.href)}
    >
      {item.label}
    </a>
  );

  return (
    <>
      <style>{SL_HEADER_CSS}</style>
      <header ref={headerRef} className={`slh${open ? " is-open" : ""}`} id="slh">
        <div className="slh-bar">
          <a className="slh-logo" href="/" aria-label="ServiceLad home" dangerouslySetInnerHTML={{ __html: LOGO_HTML }} />
          <nav className="slh-nav" aria-label="Main">
            {SL_HEADER_LINKS.map((item) => link(item, "slh-link"))}
          </nav>
          <div className="slh-actions">
            <a className="slh-cta" href={ctaHref} onClick={(event) => onLinkClick(event, ctaHref)}>{ctaLabel}<Arrow /></a>
            <button
              type="button"
              className="slh-burger"
              aria-expanded={open}
              aria-controls="slh-panel"
              aria-label={open ? "Close menu" : "Open menu"}
              onClick={() => setOpen((value) => !value)}
            >
              <span />
              <span />
            </button>
          </div>
        </div>
        <nav className="slh-panel" id="slh-panel" aria-label="Menu" hidden={!open}>
          {[...SL_HEADER_LINKS, ...SL_HEADER_MORE_LINKS].map((item) => link(item, "slh-panel-link"))}
          <a className="slh-cta slh-panel-cta" href={ctaHref} onClick={(event) => onLinkClick(event, ctaHref)}>{ctaLabel}<Arrow /></a>
        </nav>
      </header>
    </>
  );
}
